import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppInfoService } from 'core/app-info/app-info.service';
import {
  DEFAULT_PRODUCT_NOTIFICATION_BUTTONS, getProductNotificationButtons, PRODUCT_NOTIFICATION_ACTIONS, ProductNotificationAction,
  PROMO_PRICE_MESSAGE_CODE_ID
} from 'core/tooltip-notification/product-notification-priority';
import { ProductNotification } from 'src/app/modules/pos/quotes/entities/products.class';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { DeviceDetectorService } from 'ngx-device-detector';

export const PRODUCT_NOTIFICATION_DIALOG_MODES = {
  entryValidation: 'entryValidation',
  limitedMarketing: 'limitedMarketing'
} as const;
export type ProductNotificationDialogMode = typeof PRODUCT_NOTIFICATION_DIALOG_MODES[keyof typeof PRODUCT_NOTIFICATION_DIALOG_MODES];

export interface ProductNotificationDialogData {
  productNumber: string;
  notifications: ProductNotification[];
  mode: ProductNotificationDialogMode;
  messageCodes?: Record<number, string>;
  hideSupersede?: boolean;
}

export interface ProductNotificationDialogResult {
  action: ProductNotificationAction;
  supersedeProductNumber?: string;
}

const SECONDARY_ACTIONS: ProductNotificationAction[] = [PRODUCT_NOTIFICATION_ACTIONS.ok, PRODUCT_NOTIFICATION_ACTIONS.cancel];

interface PromoPrice {
  listPrice: number;
  startDate: string;
  endDate: string;
}

interface ProductNotificationMessage {
  description: string;
  promoPrice?: PromoPrice;
}

interface ProductNotificationButton {
  action: ProductNotificationAction;
  label: string;
  isPrimary: boolean;
}

@Component({
  selector: 'app-product-notification-dialog',
  templateUrl: './product-notification-dialog.component.html',
  styleUrls: ['./product-notification-dialog.component.scss'],
  standalone: false
})
export class ProductNotificationDialogComponent {
  appStrings: AppLocalization;
  buttons: ProductNotificationButton[];
  messages: ProductNotificationMessage[];
  currencySymbol: string;
  dateFormat: string;
  isMobile: boolean;
  private supersedeProductNumber: string;

  constructor(
    private dialogRef: MatDialogRef<ProductNotificationDialogComponent, ProductNotificationDialogResult>,
    private appInfoService: AppInfoService,
    private deviceService: DeviceDetectorService,
    @Inject(MAT_DIALOG_DATA) public data: ProductNotificationDialogData
  ) {
    this.appStrings = this.appInfoService.appStrings;
    this.isMobile = this.deviceService.isMobile();
    this.currencySymbol = this.appInfoService.currencySymbol;
    this.dateFormat = this.appInfoService.dateFormat;
    this.messages = data.notifications.map(notification => ({
      description: notification.description,
      promoPrice: notification.messageCodeId === PROMO_PRICE_MESSAGE_CODE_ID
        ? (notification as ProductNotification & { promoPrice?: PromoPrice }).promoPrice
        : undefined
    }));
    this.supersedeProductNumber = data.notifications[0]?.supersedeProductNumber;
    this.buttons = this.getActions().map(action => ({
      action,
      label: this.getLabel(action),
      isPrimary: !SECONDARY_ACTIONS.includes(action)
    }));
  }

  /**
   * Closes the dialog with the selected action
   * @param action - The selected action
   */
  close(action: ProductNotificationAction) {
    const result: ProductNotificationDialogResult = { action };
    if (action === PRODUCT_NOTIFICATION_ACTIONS.openSupersede) {
      result.supersedeProductNumber = this.supersedeProductNumber;
    }
    this.dialogRef.close(result);
  }

  private getActions(): ProductNotificationAction[] {
    if (this.data.mode === PRODUCT_NOTIFICATION_DIALOG_MODES.limitedMarketing) {
      return this.supersedeProductNumber
        ? [PRODUCT_NOTIFICATION_ACTIONS.ok, PRODUCT_NOTIFICATION_ACTIONS.openSupersede]
        : [PRODUCT_NOTIFICATION_ACTIONS.ok];
    }
    const firstNotification = this.data.notifications[0];
    if (!firstNotification) {
      return DEFAULT_PRODUCT_NOTIFICATION_BUTTONS;
    }
    const canOpenSupersede = !!this.supersedeProductNumber && !this.data.hideSupersede;
    return getProductNotificationButtons(firstNotification, this.data.messageCodes ?? {})
      .filter(action => action !== PRODUCT_NOTIFICATION_ACTIONS.openSupersede || canOpenSupersede);
  }

  private getLabel(action: ProductNotificationAction): string {
    switch (action) {
      case PRODUCT_NOTIFICATION_ACTIONS.cancel:
        return this.appStrings.button_cancel;
      case PRODUCT_NOTIFICATION_ACTIONS.openCurrent:
        return this.appStrings.products_notificationGoTo?.replace('{0}', this.data.productNumber);
      case PRODUCT_NOTIFICATION_ACTIONS.openSupersede:
        return this.appStrings.products_notificationGoTo?.replace('{0}', this.supersedeProductNumber);
      default:
        return this.appStrings.ok;
    }
  }
}
